import React, { useState, useEffect } from "react";

const BANNER_HEIGHT = 44;

const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Edg"))    return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari")) return "Safari";
  return "your browser";
};

const getSiteSteps = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Edg")) return [
    "Click the lock icon in the address bar",
    'Click "Permissions for this site"',
    'Set "Notifications" to "Allow"',
    "Come back to this tab and refresh",
  ];
  if (ua.includes("Chrome")) return [
    "Click the lock icon (🔒) in the address bar",
    'Click "Site settings"',
    'Set "Notifications" to "Allow"',
    "Come back to this tab and refresh",
  ];
  if (ua.includes("Firefox")) return [
    "Click the lock icon in the address bar",
    'Click "More information"',
    'Go to the "Permissions" tab',
    'Set "Send Notifications" to "Allow"',
    "Come back to this tab and refresh",
  ];
  if (ua.includes("Safari")) return [
    'In the Mac menu bar click "Safari" → "Settings for This Website…"',
    'Find "Notifications" and set it to "Allow"',
    "Come back to this tab and refresh",
    "Alternative: Safari → Settings → Websites → Notifications → find this site → Allow",
  ];
  return [
    "Click the lock or info icon in the address bar",
    'Find "Notifications" in the site settings',
    'Set it to "Allow"',
    "Come back to this tab and refresh",
  ];
};

const getOsSteps = () => {
  const browser = getBrowserName();
  const isMac = /Mac/i.test(navigator.userAgent);
  if (isMac) return {
    os: "macOS",
    steps: [
      "Open Apple menu → System Settings",
      'Click "Notifications"',
      `Find "${browser}" in the app list`,
      'Turn on "Allow Notifications"',
      "Refresh this page",
    ],
  };
  return {
    os: "Windows",
    steps: [
      "Open Start menu → Settings (⚙)",
      "Go to System → Notifications",
      `Scroll down and find "${browser}" in the app list`,
      "Turn the toggle On",
      "Refresh this page",
    ],
  };
};

const NotificationBanner = () => {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [modalOpen, setModalOpen]         = useState(false);

  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") {
      setBannerVisible(true);
      document.documentElement.style.setProperty("--notif-banner-height", BANNER_HEIGHT + "px");
    }
  }, []);

  const handleHowToEnable = () => {
    setBannerVisible(false);
    document.documentElement.style.setProperty("--notif-banner-height", "0px");
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  if (!bannerVisible && !modalOpen) return null;

  const browser = getBrowserName();
  const { os, steps: osSteps } = getOsSteps();
  const siteSteps = getSiteSteps();

  return (
    <>
      {/* ── Banner ── */}
      {bannerVisible && (
        <div
          className="notif-banner-bar"
          style={{
            position: "fixed", top: 0, right: 0, height: BANNER_HEIGHT + "px",
            zIndex: 99999, display: "flex", alignItems: "center", gap: "12px",
            padding: "0 18px", background: "#000000",
            borderBottom: "1px solid rgba(13,223,171,0.25)",
          }}
        >
          <div style={{ width: "26px", height: "26px", borderRadius: "50%", background: "rgba(13,223,171,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0DDFAB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: "11px", fontWeight: 700, color: "#0DDFAB", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap", flexShrink: 0 }}>
              Notifications
            </span>
            <div style={{ width: "1px", height: "14px", background: "rgba(255,255,255,0.15)", flexShrink: 0 }} />
            <span style={{ fontSize: "13px", color: "#b0b8cc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <strong style={{ color: "#e8eaf0", fontWeight: 500 }}>Browser notifications are not enabled.</strong>
              {" "}Enable them to receive chat notifications.
            </span>
          </div>

          <button
            onClick={handleHowToEnable}
            style={{ height: "28px", padding: "0 13px", border: "1px solid rgba(13,223,171,0.4)", borderRadius: "6px", background: "transparent", color: "#0DDFAB", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            How to enable
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Guide Modal ── */}
      {modalOpen && (
        <>
          <div className="modal fade show" style={{ display: "block", zIndex: 100010 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
              <div className="modal-content" style={{ border: "none", borderRadius: "12px", overflow: "hidden" }}>

                <div className="modal-header" style={{ borderBottom: "1px solid #f0f0f0", padding: "18px 24px 14px" }}>
                  <h5 className="modal-title" style={{ fontSize: "16px", fontWeight: 700, color: "#1d1d1d" }}>
                    Enable Notifications in {browser}
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} />
                </div>

                <div className="modal-body" style={{ padding: "20px 24px 24px" }}>

                  {/* Site Settings */}
                  <div style={{ marginBottom: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#f0fdf9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0DDFAB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
                        </svg>
                      </div>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#1d1d1d" }}>Step 1 — Site Settings</span>
                    </div>
                    <ol style={{ paddingLeft: "20px", margin: 0 }}>
                      {siteSteps.map((step, i) => (
                        <li key={i} style={{ color: "#495057", fontSize: "13px", lineHeight: "1.9" }}>{step}</li>
                      ))}
                    </ol>
                  </div>

                  <div style={{ height: "1px", background: "#f0f0f0", margin: "0 0 20px" }} />

                  {/* OS Settings */}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#f0fdf9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0DDFAB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
                        </svg>
                      </div>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#1d1d1d" }}>Step 2 — {os} System Settings</span>
                    </div>
                    <p style={{ fontSize: "12px", color: "#8c9ab0", marginBottom: "10px", lineHeight: "1.5" }}>
                      Even after allowing in the browser, {os} can block notifications at the system level. Check this too:
                    </p>
                    <ol style={{ paddingLeft: "20px", margin: 0 }}>
                      {osSteps.map((step, i) => (
                        <li key={i} style={{ color: "#495057", fontSize: "13px", lineHeight: "1.9" }}>{step}</li>
                      ))}
                    </ol>
                  </div>

                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 100009 }} onClick={closeModal} />
        </>
      )}
    </>
  );
};

export default NotificationBanner;
